import { useRef } from "react";
import { Camera, Trash2, GraduationCap } from "lucide-react";
import { toast } from "sonner";

/** Resize an image file to a small square-ish JPEG data URL (max 320px). */
export function fileToPhotoDataUrl(file, max = 320) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith("image/")) { reject(new Error("Please choose an image file.")); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read that image.")); };
    img.src = url;
  });
}

export const studentAge = (dob) => {
  if (!dob) return "";
  const b = new Date(dob + "T00:00:00"); if (Number.isNaN(b.getTime())) return "";
  const n = new Date(); let a = n.getFullYear() - b.getFullYear();
  if (n.getMonth() < b.getMonth() || (n.getMonth() === b.getMonth() && n.getDate() < b.getDate())) a -= 1;
  return a >= 0 ? `${a} yr${a === 1 ? "" : "s"}` : "";
};

/** Clickable avatar: pick / replace / remove a student photo. */
export function StudentPhotoPicker({ value, onChange, size = "h-24 w-24", testid = "student-photo", disabled = false }) {
  const ref = useRef(null);
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    try { onChange(await fileToPhotoDataUrl(f)); } catch (err) { toast.error(err.message); }
  };
  return (
    <div className="inline-flex flex-col items-center gap-1.5">
      <button type="button" disabled={disabled} onClick={() => ref.current?.click()} data-testid={`${testid}-pick`} title={disabled ? "" : value ? "Change photo" : "Add photo"}
        className={`${size} relative rounded-2xl bg-primary/10 text-primary overflow-hidden flex items-center justify-center group border border-slate-200 disabled:cursor-default`}>
        {value ? <img src={value} alt="" className="h-full w-full object-cover" data-testid={`${testid}-img`} /> : <GraduationCap className="h-1/2 w-1/2" />}
        {!disabled && (
          <span className="absolute inset-x-0 bottom-0 bg-slate-900/60 text-white text-[10px] font-semibold py-1 inline-flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="h-3 w-3" /> {value ? "Change" : "Add photo"}
          </span>
        )}
      </button>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      {value && !disabled && (
        <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`} className="text-[11px] text-slate-500 hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Remove</button>
      )}
    </div>
  );
}
